/**
 * Undo for the chart: pop the most recently added figure off the overlay stack.
 *
 * ---------------------------------------------------------------------------------------------
 * The unit of undo is an ACTION, not an overlay.
 * ---------------------------------------------------------------------------------------------
 * Almost nothing on this chart is one overlay. A Fibonacci retracement is seven line overlays, a
 * pitchfork is four, and a study can be dozens of bands or vertical lines. Popping the last
 * ELEMENT of the array would delete one band out of fifty and leave the other forty-nine behind —
 * a partial deletion that reads as a rendering bug and, for a study, can only be repaired by
 * re-running it over the network.
 *
 * So everything that goes on the chart is stamped with a `groupId` identifying the action that
 * produced it, and undo removes a whole group at a time. Drawing tools stamp their own (one per
 * figure); study overlays are stamped automatically by the overlay context, which batches every
 * add made in the same tick into one group — see ChartOverlayContext for why that is done there
 * rather than in each of the ten-odd study dialogs.
 *
 * Overlays with no groupId at all are still poppable, one at a time. Nothing on the chart should
 * be undeletable just because it arrived by a path that predates this.
 */

/**
 * Counter making group ids unique even within the same millisecond.
 *
 * `Date.now()` alone is not enough — two clicks, or a study and a drawing, can land in one
 * millisecond, and two actions sharing a groupId would be deleted together, silently taking an
 * extra figure with them.
 */
let sequence = 0;

/** A fresh group id for one action (a drawn figure, or one study run). */
export function newOverlayGroupId(kind) {
  sequence += 1;
  return `${kind || 'overlay'}-${Date.now()}-${sequence}`;
}

/** Back-compat alias: drawing tools call this to stamp a figure. */
export const newDrawingGroupId = newOverlayGroupId;

/** Whether an overlay carries a group stamp. */
export function isGrouped(overlay) {
  return !!overlay && typeof overlay.groupId === 'string' && overlay.groupId !== '';
}

/** Kept for callers that only care about tool-drawn figures. */
export const isUserDrawing = isGrouped;

/**
 * The last thing added, as the set of array indices that would come off together.
 *
 * Returns [] when there is nothing to undo. Working in indices rather than ids is what lets an
 * ungrouped overlay be popped on its own — it has no id to filter by.
 */
export function lastOverlayIndices(overlays) {
  if (!Array.isArray(overlays)) return [];

  // Scan back to the last real overlay, stepping over any holes.
  let last = -1;
  for (let i = overlays.length - 1; i >= 0; i--) {
    if (overlays[i]) { last = i; break; }
  }
  if (last === -1) return [];

  const groupId = isGrouped(overlays[last]) ? overlays[last].groupId : null;
  if (groupId === null) return [last];   // ungrouped: pops alone

  const indices = [];
  for (let i = 0; i < overlays.length; i++) {
    if (isGrouped(overlays[i]) && overlays[i].groupId === groupId) indices.push(i);
  }
  return indices;
}

/** The group id of the most recent action, or null if it had none / there is nothing. */
export function lastOverlayGroupId(overlays) {
  const indices = lastOverlayIndices(overlays);
  if (indices.length === 0) return null;
  const overlay = overlays[indices[indices.length - 1]];
  return isGrouped(overlay) ? overlay.groupId : null;
}

/** How many overlays the next undo would remove — 0 when there is nothing to undo. */
export function lastOverlaySize(overlays) {
  return lastOverlayIndices(overlays).length;
}

/** How many undoable units are on the chart (a group counts once; ungrouped count singly). */
export function countOverlayGroups(overlays) {
  if (!Array.isArray(overlays)) return 0;
  let ungrouped = 0;
  const groups = new Set();
  overlays.forEach(o => {
    if (!o) return;
    if (isGrouped(o)) groups.add(o.groupId);
    else ungrouped += 1;
  });
  return groups.size + ungrouped;
}

/**
 * Removes the most recently added figure, returning the new overlay array.
 *
 * Returns the array UNCHANGED (same reference) when there is nothing to delete, so React bails
 * out of the re-render instead of rebuilding the chart for a no-op keystroke — Ctrl+Z is easy to
 * lean on, and every press would otherwise redraw the whole canvas.
 */
export function deleteLastOverlay(overlays) {
  if (!Array.isArray(overlays)) return [];
  const indices = lastOverlayIndices(overlays);
  if (indices.length === 0) return overlays;
  const drop = new Set(indices);
  return overlays.filter((_, i) => !drop.has(i));
}

/** Back-compat alias for the original drawing-only name. */
export const deleteLastDrawing = deleteLastOverlay;

/* Older names, kept so existing call sites keep working. */
export const lastDrawingGroupId = lastOverlayGroupId;
export const lastDrawingSize = lastOverlaySize;
export const countDrawings = countOverlayGroups;
